import textsData from '@/textData/OwnerContractDetailsTexts.json'
import { useUserLanguage } from '@/customHooks';
import { useOwnerContractDetailsContext } from '@/customHooks/useOwnerContractDetailsContext';
import { OwnerContractDetail } from '@/custom-types';
import { advancedNormalizeDate, capitalizeFirstLetter, contractStatusMapper, normalizeDate, ownerContractLeaseTypeMapper, paymentFrequencyMapper } from '@/helpers';
import { Button, RowSpacer } from '../atoms';
import {
  RectangleInfoCard,
  SmallInfoCard,
  SmallInfoCardProps,
  StatsCard,
  StatsCardProps,
} from '../molecules';

import leaseStatusIcon from '@/assets/contract-icons/lease-status-icon.svg'
import monthlyRentIcon from '@/assets/contract-icons/montly-rent-icon.svg'
import balanceIcon from '@/assets/contract-icons/balance.svg'
import fixedTerm from '@/assets/contract-icons/fixed-term-icon.svg'
import linkedUnits from '@/assets/contract-icons/units-icon.svg'
import PersonIcon from '@/assets/contract-icons/person.svg'

const buildPropertyAddress = (property: OwnerContractDetail['property']): string => {
  if (!property || !property.address) return 'No data'

  const { address } = property
  const streetParts = [
    address.street,
    address.buildingNumber,
    address.addressLine,
  ].filter(Boolean)

  const locationParts = [
    address.city?.name,
    address.state?.name,
    address.country?.name,
    address.zipCode,
  ].filter(Boolean)

  const streetText = streetParts.join(' ')
  const locationText = locationParts.join(', ')

  if (streetText && locationText) return `${streetText} - ${locationText}`
  if (streetText) return streetText
  if (locationText) return locationText

  return 'No data'
}

export const OwnerContractDetailOverview = () => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es.overview : textsData.en.overview
  const propertySectionTexts = texts.propertySection

  const { contract } = useOwnerContractDetailsContext()

  const infoCards: StatsCardProps[] = [
    {
      label: texts.infoCards.card1,
      value: 0,
      iconSrc: balanceIcon,
    },
    {
      label: contract ? ownerContractLeaseTypeMapper(contract.ownerContractLeaseTypeOption, userLanguage) : 'No Data',
      value: contract ? `${normalizeDate(contract.startDate)} - ${normalizeDate(contract.finishDate)}` : 'No data',
      iconSrc: fixedTerm,
    },
    {
      label: texts.infoCards.card4,
      value: contract ? contract.contractAmount?.toString() ?? 'No data' : 'No data',
      iconSrc: balanceIcon,
    },
  ]

  const contractedUnities = contract?.property?.unities.map((unity) => {
    return {
      label: texts.infoCards.card3,
      value: capitalizeFirstLetter(unity.name),
      iconSrc: linkedUnits
    }
  }) ?? []

  const smallInfoCardData: SmallInfoCardProps[] = [
    {
      title: texts.minicards.card1,
      value: contract ? contractStatusMapper(contract.contractStatusOption, userLanguage) : 'No data',
      icon: leaseStatusIcon,
    },
    {
      title: texts.minicards.card2,
      value: contract?.contractAmount ?? 'No Data',
      icon: monthlyRentIcon,
    },
    {
      title: texts.minicards.card3,
      value: contract ? paymentFrequencyMapper(contract.paymentFrequencyOption, userLanguage) : 'No Data',
      icon: monthlyRentIcon, // Using monthlyRentIcon for frequency as well or could find another
    },
    {
      title: texts.minicards.card4,
      value: '+ Add insurance',
      icon: monthlyRentIcon,
    },
  ]

  if (!contract) return (
    <div className="flex p-6">
      <span className="font-bold">{texts.noData}</span>
    </div>
  )

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">{texts.title}</h1>
          <p className="text-sm text-gray-500">{`${texts.description} ${contract.id}`}</p>
          <p className="text-sm text-gray-500">{`${texts.finishDate} ${advancedNormalizeDate(userLanguage, contract.finishDate)}`}</p>
        </div>
        <div className="flex gap-4 w-full md:w-auto">
          <Button
            onClick={() => {}}
            secondary
            className="flex-1 md:flex-none"
          >
            {texts.buttons.button2}
          </Button>
          <Button
            onClick={() => {}}
            className="flex-1 md:flex-none"
          >
            {texts.buttons.button1}
          </Button>
        </div>
      </div>

      <RowSpacer height={3} />

      <h3 className="text-lg font-semibold mt-6 mb-3">{texts.sectionsTitles.associatedProperty}</h3>
      <div className="w-full">
        <RectangleInfoCard
          title={
            contract.property
              ? contract.property.name
              : propertySectionTexts.noPropertyTitle
          }
          subtitle={
            contract.property
              ? buildPropertyAddress(contract.property)
              : propertySectionTexts.noPropertySubtitle
          }
          iconSrc={linkedUnits}
        />
      </div>

      <RowSpacer height={3} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {infoCards.map((card, index) => (
          <StatsCard
            key={card.label + index}
            label={card.label}
            value={card.value}
            iconSrc={card.iconSrc}
          />
        ))}
      </div>

      <RowSpacer height={3} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {contractedUnities.map((card, index) => (
          <StatsCard
            key={card.label + index}
            label={card.label}
            value={card.value}
            iconSrc={card.iconSrc}
          />
        ))}
      </div>

      <RowSpacer height={3} />

      <div>
        <h3 className="text-lg font-semibold mb-6">{texts.sectionsTitles.section1}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 mt-6">
          {smallInfoCardData.map((data, index) => (
            <SmallInfoCard
              key={data.title + index}
              title={data.title}
              value={data.value}
              icon={data.icon}
            />
          ))}
        </div>
      </div>

      <RowSpacer height={3} />

      <h3 className="text-lg font-semibold mt-6 mb-3">{texts.participantsSection.title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        <div onClick={() => {}} className="cursor-pointer">
          <RectangleInfoCard
            title={texts.participantsSection.card1.title}
            subtitle={
              contract.owner
                ? `${capitalizeFirstLetter(contract.owner?.firstName)} ${capitalizeFirstLetter(contract.owner?.lastName)}`
                : texts.participantsSection.card1.noData
            }
            iconSrc={PersonIcon}
          />
        </div>
      </div>
    </div>
  )
}